import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  CheckboxField,
  CustomTagsField,
  Details,
  EmptyStation,
  type FormActionData,
  InfoPanel,
  type InitialFormData,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  ReadOnlyField,
  Section,
  SingleLineTextField,
  TextButton,
  formatDateTime,
} from '@axinom/mosaic-ui';
import { Field, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ensureError } from '../../../../common/errors/ensure-error';
import { config } from '../../../../config';
import {
  DeleteOAuthClientDocument,
  type DeleteOAuthClientMutation,
  type DeleteOAuthClientMutationVariables,
  GenerateOAuthClientSecretDocument,
  type GenerateOAuthClientSecretMutation,
  type GenerateOAuthClientSecretMutationVariables,
  GetOAuthClientDocument,
  type GetOAuthClientQuery,
  type GetOAuthClientQueryVariables,
  UpdateOAuthClientDocument,
  type UpdateOAuthClientMutation,
  type UpdateOAuthClientMutationVariables,
} from '../../../../generated/axauth-service';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { authStateStore } from '../../../../state-store';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { validateUrl } from '../../../../util/validationUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './OAuthClientDetail.module.scss';

interface OAuthClient {
  id?: string;
  name?: string;
  allowUserSignUp?: boolean;
  clientSecret?: string;
  redirectUrls?: string[];
  userStoreName?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

const oAuthClientSchema = Yup.object<ObjectSchemaDefinition<OAuthClient>>({
  name: Yup.string().label('Name').required().max(200),
  allowUserSignUp: Yup.boolean().label('Allow User Sign Up').required(),
  redirectUrls: Yup.array()
    .of(
      Yup.string().test(
        'allowed-redirect-url',
        'Allowed Redirect URLs must contain valid URLs',
        validateUrl,
      ),
    )
    .label('Allowed Redirect URLs')
    .min(1, 'Allowed Redirect URLs must include at least one URL'),
});

export const OAuthClientDetail: React.FC = () => {
  const { envId, userStoreId, clientId } = useParams<{
    envId: string;
    userStoreId: string;
    clientId: string;
  }>();

  const [data, setData] = useState<OAuthClient>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const [showClientSecret, setShowClientSecret] = useState<boolean>(false);

  const fetchClientData = useCallback(async (): Promise<void> => {
    try {
      try {
        // Get extra client data (Client audit data and User Store name)
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
        );

        const oAuthClientData = await envApolloClient.query<
          GetOAuthClientQuery,
          GetOAuthClientQueryVariables
        >({
          query: GetOAuthClientDocument,
          variables: {
            id: clientId,
          },
          fetchPolicy: 'no-cache',
        });

        setData({
          id: oAuthClientData.data.oAuthClient.clientId,
          name: oAuthClientData.data.oAuthClient.clientName,
          allowUserSignUp: oAuthClientData.data.oAuthClient.allowUserSignUp,
          clientSecret: oAuthClientData.data.oAuthClient.clientSecret,
          redirectUrls: oAuthClientData.data.oAuthClient.redirectUris ?? [],
          userStoreName: oAuthClientData.data.oAuthClient.userStoreName,
          createdAt: oAuthClientData.data.oAuthClient.createdAt,
          createdBy: oAuthClientData.data.oAuthClient.createdBy ?? '',
          updatedAt: oAuthClientData.data.oAuthClient.updatedAt,
          updatedBy: oAuthClientData.data.oAuthClient.updatedBy ?? '',
        });
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      }
    } catch (e) {
      const error = ensureError(e);
      setError(error.message);
    }
  }, [clientId, envId]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          await fetchClientData();
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, fetchClientData]);

  const { actions, newClientSecret } = useActions(envId, userStoreId, clientId);

  useEffect(() => {
    if (newClientSecret !== undefined) {
      // Fetch updated client data
      (async () => {
        await fetchClientData();
      })();

      // Show the new client secret
      setShowClientSecret(true);
    }
  }, [fetchClientData, newClientSecret]);

  const onSubmit = useCallback(
    async (
      formData: OAuthClient,
      initialData: InitialFormData<OAuthClient>,
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      // Update the client-registration
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      await envApolloClient.mutate<
        UpdateOAuthClientMutation,
        UpdateOAuthClientMutationVariables
      >({
        mutation: UpdateOAuthClientDocument,
        variables: {
          input: {
            clientId: clientId,
            clientName: formData.name,
            allowUserSignUp:
              formData.allowUserSignUp !== undefined
                ? formData.allowUserSignUp
                : undefined,
            redirectUris: formData.redirectUrls,
          },
        },
        fetchPolicy: 'no-cache',
      });
    },
    [clientId, envId],
  );

  const Panel: React.FC<{ envId: string }> = ({ envId }) => {
    const { values } = useFormikContext<OAuthClient>();
    const tenantId = authStateStore.useState('tenantId');

    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Client ID">{values.id}</Paragraph>
          <Paragraph title="Created">
            {formatDateTime(values.createdAt ?? '')} by {values.createdBy}
          </Paragraph>
          <Paragraph title="Last Modified">
            {formatDateTime(values.updatedAt ?? '')} by {values.updatedBy}
          </Paragraph>
        </Section>
        <Section>
          <Paragraph title="Tenant ID">{tenantId}</Paragraph>
          <Paragraph title="Environment ID">{envId}</Paragraph>
          <Paragraph title="User Store Name">{values.userStoreName}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    const { values } = useFormikContext<OAuthClient>();
    const clientSecretMask =
      '**************************************************************************************';

    const showHideClientSecret = (): void => {
      setShowClientSecret((current) => !current);
    };

    const copyClientSecret = (): void => {
      try {
        // copy secret to clipboard
        navigator.clipboard.writeText(values.clientSecret ?? '');
      } catch (_error) {
        // eslint-disable-next-line no-console
        console.warn('Failed to copy to clipboard');
      }
    };

    return (
      <>
        <Field name="name" label="Name" as={SingleLineTextField} />
        <Field
          name="allowUserSignUp"
          label="Allow User Sign Up"
          as={CheckboxField}
        />
        <Field name="id" label="Client ID" as={ReadOnlyField} />
        <div className={classes.secret}>
          <ReadOnlyField
            value={showClientSecret ? values.clientSecret : clientSecretMask}
            label="Client Secret"
          />
          <TextButton
            className={classes.button}
            text={showClientSecret ? 'Hide' : 'Show'}
            onButtonClicked={showHideClientSecret}
          />
          <TextButton
            className={classes.button}
            text="Copy"
            onButtonClicked={copyClientSecret}
          />
        </div>
        <Field
          name="redirectUrls"
          label="Allowed Redirect URLs"
          as={CustomTagsField}
        />
      </>
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="OAuth Client Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<OAuthClient>
          defaultTitle="OAuth Client"
          titleProperty="name"
          subtitle="OAuth Client Details"
          actions={actions}
          validationSchema={oAuthClientSchema}
          initialData={{
            data,
            loading,
            error: error,
          }}
          saveData={onSubmit}
          infoPanel={<Panel envId={envId} />}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function useActions(envId: string, userStoreId: string, clientId: string) {
  const history = useHistory();
  const [newClientSecret, setNewClientSecret] = useState<string>();

  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
  );

  const actions: FormActionData[] = [
    {
      label: 'Re-Generate Client Secret',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Once you generate a new client secret, all usages of the previous client secret will become invalid and would need to be updated with the new value manually.',
      },
      onActionSelected: async () => {
        const response = await envApolloClient?.mutate<
          GenerateOAuthClientSecretMutation,
          GenerateOAuthClientSecretMutationVariables
        >({
          mutation: GenerateOAuthClientSecretDocument,
          variables: {
            input: {
              clientId,
            },
          },
          fetchPolicy: 'no-cache',
        });

        setNewClientSecret(
          response.data?.generateOAuthClientSecret.newClientSecret,
        );
      },
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        await envApolloClient?.mutate<
          DeleteOAuthClientMutation,
          DeleteOAuthClientMutationVariables
        >({
          mutation: DeleteOAuthClientDocument,
          variables: {
            input: {
              clientId,
              userStoreId,
            },
          },
          fetchPolicy: 'no-cache',
        });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients`,
        );
      },
    },
  ];

  return { actions, newClientSecret } as const;
}

export const OAuthClientDetailCrumb: BreadcrumbResolver = ({
  envId,
  clientId,
}) => {
  const envApolloClient = environmentApolloClient(
    envId,
    new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
  );

  return async (): Promise<string> => {
    const response = await envApolloClient.query<GetOAuthClientQuery>({
      query: GetOAuthClientDocument,
      variables: { id: clientId },
      errorPolicy: 'ignore',
    });
    return response.data.oAuthClient.clientName;
  };
};
